import { useCallback, useMemo, useState } from "react"
import { MenuItem } from "@mui/material"
import { useSnackbar } from "notistack"
import { DateTime } from "luxon"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { Receipt, ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { Order } from "../../../controlKitchenTypes"
import { Subscription, cancelSubscription, fetchSubscriptionByPeriod, AccountingQuery } from "../accountingApi"
import { CancelSubscriptionConfirmDialog } from "../../../../../shared/components/subscriptionsTable/CancelSubscriptionConfirmDialog"
import { SubscriptionsTable } from "../../../../../shared/components/subscriptionsTable/SubscriptionsTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useMerchant } from "../../../../merchants/merchantHooks"
import {
    defaultSubscriptionsFilterOptionsList,
    FiltersDrawerSubscriptions,
    SubscriptionsFilterProps,
} from "../../../../supplier/reporting/accounting/subscriptions/components/FiltersDrawersSubscriptions"
import useActiveFiltersCount from "../../../../supplier/reporting/accounting/subscriptions/hooks/useActiveFiltersCount"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardFilterButton } from "../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { StandardCustomerSelect } from "../../../../../shared/components/filters/components/StandardCustomerSelect"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useDateTime } from "../../../../localization/useDateTime"

type SubscriptionAccountingTabProps = Readonly<{}>

export function SubscriptionAccountingTab({}: SubscriptionAccountingTabProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const todayDate = dateTimeFactory.now()

    const [subscriptionToCancel, setSubscriptionToCancel] = useState<Subscription>()
    const [receipt, setReceipt] = useState<Receipt>()

    const { merchantId } = useMerchant()

    const { enqueueSnackbar } = useSnackbar()

    const [drawerOpen, setDrawerOpen] = useState(false)

    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<SubscriptionsFilterProps>({
        ...defaultSubscriptionsFilterOptionsList,
        merchantId: merchantId.toString(),
        startDate,
        endDate,
    })
    const { activeCount } = useActiveFiltersCount(filtersList)

    const subscriptionReport = useApiQuery(fetchSubscriptionByPeriod, {
        queryName: AccountingQuery.SUBSCRIPTIONS_LIST,
        dependencies: {
            ...filtersList,
            merchantId,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            subscriptionIds: filtersList.subscriptionIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: filtersList.startDate?.toISODate(),
            endDate: filtersList.endDate?.toISODate(),
        },
    })

    const tableData = useMemo(() => {
        return subscriptionReport.data?.subscriptions
    }, [subscriptionReport.data])

    const showReceipt = useCallback((row: Order | { id: number; uid: string; orderType: string }) => {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }, [])

    const { mutate: callCancelSubscription } = useApiMutation(cancelSubscription, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [AccountingQuery.SUBSCRIPTIONS_LIST] })
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("accounting-subscription-cancel-true"),
            })
        },
    })

    return (
        <>
            <SubscriptionsTable
                tableData={tableData ?? []}
                tableState={{
                    isLoading: subscriptionReport.isLoading,
                    isFetching: subscriptionReport.isFetching,
                    isError: subscriptionReport.isError,
                }}
                toolbarInternalActions={
                    <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                }
                topToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter
                            value={{
                                startDate: filtersList.startDate,
                                endDate: filtersList.endDate,
                            }}
                            onChange={setFiltersList}
                        />
                        <StandardCustomerSelect
                            setCustomerId={(customerId) =>
                                setFiltersList((prev) => ({
                                    ...prev,
                                    customerId,
                                }))
                            }
                            setCustomerGroupId={(userGroupId) =>
                                setFiltersList((prev) => ({
                                    ...prev,
                                    userGroupId,
                                }))
                            }
                            customerId={filtersList.customerId}
                            customerGroupId={filtersList.userGroupId}
                        />
                    </StandardTableFilter>
                )}
                rowActionMenuItems={({ row, closeMenu }) => [
                    <MenuItem
                        key={`receipt-${row.original.subscriptionDetails.order.id}`}
                        onClick={() => {
                            showReceipt(row.original.subscriptionDetails.order)
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="office-purchase-receipt">
                            <span>Show receipt</span>
                        </LocalizedStrict>
                    </MenuItem>,

                    <MenuItem
                        key={`cancel-${row.original.subscriptionDetails.order.id}`}
                        onClick={() => {
                            setSubscriptionToCancel(row.original)
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="accounting-subscription-cancel">
                            <span>Cancel subscription</span>
                        </LocalizedStrict>
                    </MenuItem>,
                ]}
                dateState={{
                    startDate: filtersList.startDate ?? todayDate,
                    endDate: filtersList.endDate ?? todayDate,
                }}
                setDateState={() => {}}
            />

            {receipt && (
                <ReceiptDialog receipt={receipt} open={receipt !== undefined} onClose={() => setReceipt(undefined)} />
            )}
            {subscriptionToCancel && (
                <CancelSubscriptionConfirmDialog
                    subscriptionName={subscriptionToCancel.name}
                    customerName={subscriptionToCancel.subscriptionDetails.customer.displayName}
                    open={Boolean(subscriptionToCancel)}
                    onClose={() => setSubscriptionToCancel(undefined)}
                    onCancelSubscription={(ignoreTerms) => {
                        callCancelSubscription({
                            orderId: subscriptionToCancel.subscriptionDetails.order.id,
                            ignoreTerms,
                        })
                    }}
                />
            )}

            <FiltersDrawerSubscriptions
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
